import { useRef, useState } from 'react';

import classNames from 'classnames';
import { observer } from 'mobx-react';

import { useCoinsAnimation } from '../../../hooks/useCoinsAnimation';
import { analytics } from '../../../services/analytics';
import { isFreeSpinsBundleItem } from '../../../stores/rewards/helpers';
import type { BundleItem } from '../../../stores/rewards/types';
import type { ClaimableReward } from '../../../stores/ui/types';
import { useStores } from '../../../stores/useStores';
import { devError } from '../../../utils/log';
import { ONBOARDING_CONFIG } from '../../onboarding/constants';
import { DailyBonusOnboarding } from '../../onboarding/DailyBonusOnboarding';
import { useOnboarding } from '../../onboarding/useOnboarding';

import BonusCard from './BonusCard';
import classes from './BonusCards.module.scss';

const BonusCards = observer(() => {
  const { portalStore, rewardsStore, onboardingStore, featureManagerStore } = useStores();

  const animationIsRunning = useRef(false);

  const [isClaiming, setIsClaiming] = useState(false);

  const { showOnboarding, setReferenceElement } = useOnboarding(ONBOARDING_CONFIG.BONUS_CLAIMING, 'top-start', [0, 16]);
  const { startAnimation } = useCoinsAnimation();

  const close = () => {
    setIsClaiming(false);
    portalStore.closePortal();
    featureManagerStore.onDialogFinished('DailyBonus');
  };

  async function claimBonus(onEnd: () => void) {
    try {
      const response = await rewardsStore.claimDailyBonus();
      if (response?.data) {
        const bundleItems: BundleItem[] = [];

        if (response.data.dailyPackageData?.[0]) {
          const item = response.data.dailyPackageData[0];

          if (isFreeSpinsBundleItem(item) && ['Barouletta Gaming', 'Minigames'].includes(item.bundlePayload.provider)) {
            bundleItems.push(item);
          }
        }

        const rewards: ClaimableReward[] = [
          { scCoin: response.data.sc_amount, gcCoin: response.data.gc_amount, bundleItems },
        ];

        startAnimation({
          rewards,
          onEnd: () => {
            setTimeout(() => {
              onEnd();
            }, 1500);
          },
        });
      }
    } catch (e) {
      devError('Error claiming daily bonus', e);
      setIsClaiming(false);
    }
  }

  const handleBonusDayClaim = async () => {
    if (onboardingStore.step > -1 && animationIsRunning.current) {
      return;
    }

    setIsClaiming(true);

    if (rewardsStore.dailyBonus?.isEntitledToBonus || rewardsStore.dailyBonus?.timeUntilNextBonusMS === 0) {
      const currentClaimDay = rewardsStore.dailyBonus?.currentDay || 0;

      if (onboardingStore.step === ONBOARDING_CONFIG.DAILY_BONUS) {
        animationIsRunning.current = true;

        void analytics.sendEvent('onboarding_next_step_click', {
          step: 'claim_bonus',
        });

        await claimBonus(() => {
          setIsClaiming(false);
          onboardingStore.nextStep(); // -> CLAIM_REWARD

          setTimeout(() => {
            void analytics.sendEvent('onboarding_next_step_click', {
              step: 'bonus_claimed',
            });
            onboardingStore.nextStep(); // -> START_GAME
            portalStore.closePortal();
            animationIsRunning.current = false;
          }, 60);
        });
      } else {
        await claimBonus(() => {
          close();
        });
      }

      void analytics.sendEvent('daily_bonus' as any, {
        event_action: 'click',
        click_location: 'popup',
        claimed_day: currentClaimDay + 1,
      });
    } else {
      close();
    }
  };

  const className = classNames(classes.bonusCardsWrapper);

  return (
    <div ref={setReferenceElement} className={className}>
      {rewardsStore.dailyBonusesList?.map((item) => {
        const currDay = rewardsStore?.dailyBonus?.currentDay || 0;
        return (
          <BonusCard
            key={item.day}
            claimed={(rewardsStore?.dailyBonus?.currentDay || 0) >= item?.day}
            data={item}
            handleBonusDayClaim={handleBonusDayClaim}
            isClaiming={isClaiming}
            isEntitledToBonus={rewardsStore?.dailyBonus?.isEntitledToBonus && currDay + 1 === item?.day}
          />
        );
      })}
      {showOnboarding && <DailyBonusOnboarding />}
    </div>
  );
});

export default BonusCards;
